/**
 * 资源下载页百度统计埋点（与 freeDownloadStat / 设计文档对齐）
 *
 * 二期：点击「立即下载 / 申请获取」必须带 tabCode、bizId，
 * 供百度受访页 → url_map/规则 → li_stat_resource 归一。
 *
 * 虚拟页路径约定（iframe download_hit）：
 *   download_hit#/freedownload?event=package_action_click&platSource=developer&channel=pc&module=freedownload&tabCode=...&bizCode=soft_package&bizId=...
 * Tab 曝光：
 *   download_hit#/freedownload?event=tab_view&...&tabCode=desktop_x86&bizCode=tab
 */

export type PackageActionType = "download" | "apply";

export type BaiduPackageActionPayload = {
  tabCode: string;
  bizId: string;
  actionType?: PackageActionType;
  bizCode?: string;
  platSource?: string;
  channel?: "pc" | "h5";
  module?: string;
  pagePath?: string;
  /** 兼容旧下载量采集：OBS 路径片段，如 freedownload/v1.2/... */
  legacyDownloadPath?: string;
};

export type BaiduTabViewPayload = {
  tabCode: string;
  channel?: "pc" | "h5";
  platSource?: string;
  module?: string;
};

const IFRAME_ID = "hm_iframe";

function setHmIframeSrc(iframeSrc: string) {
  const myIframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
  if (!myIframe) return;
  if (myIframe.src.includes("download_hit")) {
    myIframe.src = iframeSrc;
    myIframe.contentWindow?.location.reload();
  } else {
    myIframe.src = iframeSrc;
  }
}

function buildQuery(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && String(value) !== "") {
      search.set(key, String(value));
    }
  });
  return search.toString();
}

/** 旧链路：路径型 download_hit，供历史下载量统计继续使用 */
export function trackBaiduLegacyDownloadPath(pathFragment: string) {
  if (!pathFragment) return;
  const normalized = pathFragment.replace(/^#/, "");
  setHmIframeSrc(`download_hit#${normalized}`);
}

/**
 * 包操作点击埋点（设计文档 7.3）
 * 同时可选保留 legacyDownloadPath，短暂延迟二次上报以免覆盖。
 */
export function trackBaiduPackageActionClick(payload: BaiduPackageActionPayload) {
  if (!payload?.bizId || !payload?.tabCode) return;

  const channel = payload.channel || "pc";
  const moduleName = payload.module || "freedownload";
  const pagePath = payload.pagePath || "/freedownload";
  const qs = buildQuery({
    event: "package_action_click",
    platSource: payload.platSource || "developer",
    channel,
    module: moduleName,
    tabCode: payload.tabCode,
    bizCode: payload.bizCode || "soft_package",
    bizId: payload.bizId,
    actionType: payload.actionType || "download",
    pagePath,
    pageUrl: `${pagePath}#${payload.tabCode}`,
  });

  const reportStructured = () => {
    setHmIframeSrc(`download_hit#${pagePath}?${qs}`);
  };

  if (payload.legacyDownloadPath) {
    trackBaiduLegacyDownloadPath(payload.legacyDownloadPath);
    window.setTimeout(reportStructured, 300);
  } else {
    reportStructured();
  }
}

/** Tab 曝光 / 进入区块，便于同页多 Tab 拆分 PV */
export function trackBaiduTabView(payload: BaiduTabViewPayload) {
  if (!payload?.tabCode) return;
  const pagePath = "/freedownload";
  const qs = buildQuery({
    event: "tab_view",
    platSource: payload.platSource || "developer",
    channel: payload.channel || "pc",
    module: payload.module || "freedownload",
    tabCode: payload.tabCode,
    bizCode: "tab",
    bizId: payload.tabCode,
    pagePath,
    pageUrl: `${pagePath}#${payload.tabCode}`,
  });
  setHmIframeSrc(`download_hit#${pagePath}?${qs}`);
}

/** 从下载 key / 卡片配置解析稳定 bizId */
export function resolvePackageBizId(item: {
  bizId?: string;
  download?: string;
  link?: string;
  title?: string;
}): string {
  if (item?.bizId) return item.bizId;
  if (item?.download) return item.download;
  if (item?.link) return item.link;
  if (item?.title) return item.title;
  return "";
}

export const DESKTOP_TAB_CODE = {
  x86: "desktop_x86",
  arm: "desktop_arm",
} as const;
